import TextArea from '@ui/text-area/text-area';
import { ICourse, ICourseCertificate, ICreateCourseCertificate } from '@/types/courses';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { useState, useEffect } from 'react';
import CourseEditCertificateModal from '../modals/course-edit-certificate-modal';
import CourseEmailPreviewModal from '../modals/course-email-preview-modal';
import { ICONS } from '@ui/icons/types';
import Icon from '@ui/icons/icon';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { UpdateCourseCertificate } from '@/connection/courses';
import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import { selectWorkingProjectDefaultLanguage } from '@/store/slices/project/selectors';
import useLanguageParams from '@/utils/use-language-params';
import { EToastTypes } from '@ui/toast/toast.types';

type CourseCertificateProps = {
  savingCertificate: boolean;
  course: ICourse | null;
  workingCertificate: ICourseCertificate | ICreateCourseCertificate | undefined;
  onCertificateUpdate: (cert: ICourseCertificate) => void;
};

function CourseCertificate({
  course,
  workingCertificate,
  savingCertificate,
  onCertificateUpdate,
}: CourseCertificateProps) {
  const user = useAppSelector(getUser);
  const defaultLang = useAppSelector(selectWorkingProjectDefaultLanguage) || 'en';

  const [designModalOpen, setDesignModalOpen] = useState(false);
  const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState(false);
  const [localWorkingCertificate, setLocalWorkingCertificate] = useState<
    ICourseCertificate | ICreateCourseCertificate | undefined
  >(workingCertificate);
  const [saving, setSaving] = useState(false);

  const language = useLanguageParams();

  const activeChannel = user?.active_channel;

  const toggleDesignModal = () => setDesignModalOpen((prevState) => !prevState);
  const toggleEmailPreview = () => setEmailPreviewModalOpen((prevState) => !prevState);

  useEffect(() => {
    setLocalWorkingCertificate(workingCertificate);
  }, [workingCertificate]);

  const handleDesignEdit = (cert?: ICourseCertificate) => {
    toggleDesignModal();
    if (cert) {
      onCertificateUpdate(cert);
    }
  };

  const saveCertificate = async () => {
    if (!localWorkingCertificate || !activeChannel || saving) return;

    if (!('uuid' in localWorkingCertificate)) return;

    try {
      setSaving(true);
      const updatedCert = await UpdateCourseCertificate(
        activeChannel,
        localWorkingCertificate.uuid,
        localWorkingCertificate
      );
      onCertificateUpdate(updatedCert);
    } catch (e) {
      console.error(e);
      toastTrigger('Failed to save certificate', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCertificateEmailSubjectChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (localWorkingCertificate) {
      setLocalWorkingCertificate({
        ...localWorkingCertificate,
        email_subject: updateTranslateKey({
          translateString: localWorkingCertificate.email_subject,
          input: e.target.value,
          baseLanguage: language || defaultLang,
          language: language || defaultLang,
        }),
      });
    }
  };

  const handleCertificateEmailBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (localWorkingCertificate) {
      setLocalWorkingCertificate({
        ...localWorkingCertificate,
        email_body: updateTranslateKey({
          translateString: localWorkingCertificate.email_body,
          input: e.target.value,
          baseLanguage: language || defaultLang,
          language: language || defaultLang,
        }),
      });
    }
  };

  return (
    <>
      <Container className="flex gap-4 ai-center">
        <Text className="H5" color="var(--gray-100)">
          Certificate
        </Text>
        <Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--gray-500)" />
      </Container>
      <Container
        data-testid={`course-edit-certificate-tab`}
        className="certificate-tab-container flex gap-16"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 300px',
          height: '234px',
        }}
      >
        <Container design="GlobalCont2" className="padding-h-16 padding-b-16 padding-t-8 flex-column gap-10 radius-12">
          <Container className="flex ai-center jc-space-between">
            <Text className="H6" color="var(--gray-100)">
              Email
            </Text>
            <Button
              className="small no-style no-padding flex ai-center gap-8 padding-r-0"
              data-testid={`course-edit-certificate-preview-btn`}
              disabled={!localWorkingCertificate}
              onClick={toggleEmailPreview}
            >
              <Text className="L2 flex ai-center gap-8" color={getThemeColorCSSVariable()}>
                <Icon name={ICONS.VIEWERS_EYE_SOLID} size={16} />
                <span>Preview</span>
              </Text>
            </Button>
          </Container>
          <Container>
            <Text className="L2 margin-b-4 margin-l-16" color="var(--global-ico-text-3)">
              Subject
            </Text>
            {!localWorkingCertificate ? (
              <Text className="S2 margin-l-16" color="var(--gray-600)">
                Choose a design first
              </Text>
            ) : (
              <TextArea
                maxRows={2}
                placeholder="Enter email subject"
                value={localWorkingCertificate?.email_subject.base ?? ''}
                onChange={handleCertificateEmailSubjectChange}
                onBlur={saveCertificate}
                disabled={saving || !localWorkingCertificate}
                data-testid="course-edit-certificate-email-subject-input"
                onKeyDown={(e) => (e.key === 'Enter' ? e.preventDefault() : null)}
              />
            )}
          </Container>
          <Container>
            <Text className="L2 margin-b-4 margin-l-16" color="var(--global-ico-text-3)">
              Email message
            </Text>
            {!localWorkingCertificate ? (
              <Text className="S2 margin-l-16" color="var(--gray-600)">
                Choose a design first
              </Text>
            ) : (
              <TextArea
                maxRows={4}
                placeholder="Enter email message"
                value={localWorkingCertificate?.email_body.base ?? ''}
                onChange={handleCertificateEmailBodyChange}
                onBlur={saveCertificate}
                disabled={saving || !localWorkingCertificate}
                data-testid="course-edit-certificate-email-body-input"
              />
            )}
          </Container>
        </Container>
        <Container design="GlobalCont2" className="padding-16 flex-column gap-10 radius-12">
          <Text className="H6" color="var(--gray-100)">
            Design
          </Text>
          {course?.certificate?.design?.thumbnail ? (
            <Container background="var(--gray-900)" width="fit-content" className="margin-auto">
              <Button
                width="fit-content"
                height="fit-content"
                disabled={savingCertificate}
                className="padding-0"
                onClick={toggleDesignModal}
                data-testid={`course-edit-certificate-design-edit-btn`}
              >
                <img
                  src={course.certificate.design.thumbnail}
                  className="radius-8 cursor-pointer"
                  style={{ maxHeight: '168px', width: 'auto' }}
                />
              </Button>
            </Container>
          ) : (
            <Button
              width="100%"
              height="100%"
              className="flex ai-center jc-center radius-8 cursor-pointer flex ai-center gap-4"
              border="1px dashed var(--gray-500)"
              onClick={toggleDesignModal}
              data-testid={`course-edit-certificate-design-choose-btn`}
            >
              <Text className="L2 flex ai-center gap-8">
                <Icon name={ICONS.ADD} size={12} />
                <span>Choose Design</span>
              </Text>
            </Button>
          )}
        </Container>
      </Container>
      <CourseEditCertificateModal onComplete={handleDesignEdit} open={designModalOpen} course={course} />
      <CourseEmailPreviewModal
        open={emailPreviewModalOpen}
        certificate={localWorkingCertificate}
        onRequestClose={toggleEmailPreview}
        courseName={course?.name?.base || undefined}
      />
    </>
  );
}

export default CourseCertificate;
